import { Card } from '@cmsgov/ds-medicare-gov'
import { Button } from 'components/Button/Button'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ClaimDetailsRequest, ClaimSummary } from '@cms-max/medicare-api-sdk'
import { useClaimTypeDisplay } from 'hooks/useClaimTypeDisplay'
import { ApprovedChargesCardDisplay } from './ApprovedChargesCardDisplay'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'

export interface ClaimCardProps {
  claim: ClaimSummary
  className?: string
  onShowDetails: (request: ClaimDetailsRequest) => void
}

export const ClaimCard: FC<ClaimCardProps> = ({ claim, className, onShowDetails }) => {
  const { t } = useTranslation('claimsSearch', { keyPrefix: 'claimCard' })
  const { t: tCommon } = useTranslation('common')
  const {
    accretionDate,
    claimNumber,
    claimToken,
    claimType,
    contractNumber,
    displayClaimNumber,
    endDate,
    entryCode,
    medicareApprovedCharges,
    medicareApprovedInfo,
    medicarePaidToProvider,
    provider,
    startDate,
    youMayBeBilled
  } = claim
  const detailsRequest: ClaimDetailsRequest = {
    accretionDate,
    claimNumber,
    claimType,
    contractNumber,
    entryCode,
    token: claimToken,
    youMayBeBilled: claim.youMayBeBilled
  }
  const claimTypeDisplay = useClaimTypeDisplay(claim.claimType)

  return (
    <Card className={className}>
      <div className='ds-u-padding--3 my-c-claim-card-summary'>
        <div>
          <dl className='my-c-claim-card-info'>
            <dt>{t('serviceDates')}</dt>
            <dd>{tCommon('dateRange', { startDate, endDate })}</dd>

            <dt>{t('claimType')}</dt>
            <dd>{claimTypeDisplay}</dd>
          </dl>
        </div>
        <div>
          <dl className='my-c-claim-card-info'>
            <dt>{t('provider')}</dt>
            <dd>{provider}</dd>

            <dt>{t('claimNumber')}</dt>
            <dd>{displayClaimNumber}</dd>
          </dl>
        </div>
        <div>
          <dl className='my-c-claim-card-costs'>
            <div>
              <dt>{t('medicareApprovedAmount')}</dt>
              <dd>
                <ApprovedChargesCardDisplay
                  medicareApprovedCharges={medicareApprovedCharges}
                  medicareApprovedInfo={medicareApprovedInfo}
                />
              </dd>
            </div>
            <div>
              <dt>{t('medicarePaid')}</dt>
              <dd>{medicarePaidToProvider}</dd>
            </div>
            <div>
              <dt>{t('youMayBeBilled')}</dt>
              <dd>{youMayBeBilled}</dd>
            </div>
          </dl>
        </div>
      </div>
      <hr className='ds-u-margin--0 ds-u-visibility--hidden-print' />
      <div className='ds-u-padding--3 ds-u-visibility--hidden-print'>
        <Button
          data-testid='see-claim-details'
          className='ds-c-button ds-c-button--solid ds-c-button--alternate'
          onClick={() => onShowDetails(detailsRequest)}
          aria-label={`${t('seeClaimDetails')} ${t('claimDetailsFor')} ${detailsRequest.claimNumber}`}
          siteActivityProps={{
            core: {
              component: 'Claims',
              elementId: 'claims_claimdetails_click',
              coreClickData: {
                linkText: t('seeClaimDetails'),
                linkUrl: buildAbsoluteMedicareMyUrl('/claims/details')
              }
            }
          }}
        >
          {t('seeClaimDetails')}
        </Button>
      </div>
    </Card>
  )
}
